/* eslint-disable camelcase */
// What sits behind "More options".
//
// The test is whether a schedule is complete and correct without it. Narrowing
// a CRM report to particular lead sources or team members is a refinement of a
// report that already works; a custom date range on a client report cannot be
// scheduled at all and exists for previewing and downloading. Neither belongs
// in the path a user walks every time, so both are here, closed by default —
// and reopened automatically whenever a saved schedule actually uses them.
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
// en-gb gives the pickers day-first entry and display. Without it the adapter
// falls back to the US order, where 25/12/2026 is rejected outright and
// 03/09/2026 silently parses as 9 March.
import 'dayjs/locale/en-gb';
import { Alert, Autocomplete, Chip, FormControlLabel, Stack, Switch, TextField, Typography, alpha } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LEAD_SOURCE_TYPES } from 'src/constants/LeadSourceConst';
import { UK_DATE_FORMAT } from './reportSchedulePresentation';
import { CLIENT, LEGACY } from './scheduleRows';

const userLabel = (user) => {
    const first = user?.user?.first_name || '';
    const last = user?.user?.last_name || '';
    return `${first} ${last}`.trim() || user?.user?.email || `User #${user?.id}`;
};

export default function ScheduleAdvancedFields({ kind, form, update, busy, practiceUsers }) {
    const scope = form.additional_context || {};
    const setScope = (patch) => update('additional_context', { ...scope, ...patch });

    if (kind === LEGACY) {
        const narrowsSources = form.report_type === 'BY_SOURCE_SUMMARY';
        const narrowsUsers = form.report_type === 'USER_PERFORMANCE';
        if (!narrowsSources && !narrowsUsers) {
            return (
                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                    This report type has no extra settings.
                </Typography>
            );
        }
        return (
            <Stack spacing={1.5}>
                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                    Narrow this report to specific {narrowsSources ? 'lead sources' : 'team members'}. Leave empty to
                    include everything.
                </Typography>
                {narrowsSources && (
                    <Autocomplete
                        multiple
                        size="small"
                        options={LEAD_SOURCE_TYPES}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        disabled={busy}
                        value={LEAD_SOURCE_TYPES.filter((option) =>
                            (scope.source_types || []).includes(option.value)
                        )}
                        onChange={(event, value) => setScope({ source_types: value.map((option) => option.value) })}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    key={option.value}
                                    label={option.label}
                                    size="small"
                                    {...getTagProps({ index })}
                                    sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', fontWeight: 600 }}
                                />
                            ))
                        }
                        renderInput={(params) => <TextField {...params} label="Lead sources" placeholder="All sources" />}
                    />
                )}
                {narrowsUsers && (
                    <Autocomplete
                        multiple
                        size="small"
                        options={practiceUsers}
                        getOptionLabel={userLabel}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disabled={busy}
                        value={practiceUsers.filter((user) =>
                            (scope.practice_user_ids || []).includes(user.id)
                        )}
                        onChange={(event, value) => setScope({ practice_user_ids: value.map((user) => user.id) })}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    key={option.id}
                                    label={userLabel(option)}
                                    size="small"
                                    {...getTagProps({ index })}
                                    sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', fontWeight: 600 }}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Team members" placeholder="All team members" />
                        )}
                    />
                )}
            </Stack>
        );
    }

    if (kind !== CLIENT) return null;

    const custom = form.period === 'custom';
    return (
        <Stack spacing={2}>
            <FormControlLabel
                control={
                    <Switch
                        checked={custom}
                        disabled={busy}
                        onChange={(event) =>
                            event.target.checked
                                ? update('period', 'custom')
                                : update({ period: 'previous_month', date_from: '', date_to: '' })
                        }
                    />
                }
                label="Use a custom date range (cannot be scheduled)"
            />
            {custom && (
                <Alert severity="info">
                    A custom range cannot be saved as a repeating schedule. Preview or generate the downloads, then
                    switch this off to schedule the report.
                </Alert>
            )}
            {custom && (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <DatePicker
                            label="Start date (inclusive)"
                            format={UK_DATE_FORMAT}
                            value={form.date_from ? dayjs(form.date_from) : null}
                            disabled={busy}
                            onChange={(date) => update('date_from', date?.isValid() ? date.format('YYYY-MM-DD') : '')}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                        <DatePicker
                            label="End date (inclusive)"
                            format={UK_DATE_FORMAT}
                            value={form.date_to ? dayjs(form.date_to) : null}
                            disabled={busy}
                            onChange={(date) => update('date_to', date?.isValid() ? date.format('YYYY-MM-DD') : '')}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Stack>
                </LocalizationProvider>
            )}
        </Stack>
    );
}

ScheduleAdvancedFields.propTypes = {
    kind: PropTypes.string.isRequired,
    form: PropTypes.object.isRequired,
    update: PropTypes.func.isRequired,
    busy: PropTypes.bool,
    practiceUsers: PropTypes.array,
};

ScheduleAdvancedFields.defaultProps = {
    practiceUsers: [],
};
